import { useState } from 'react';
import { Alert, Button, Chip, Stack, Typography } from '@mui/material';
import AddToDriveRounded from '@mui/icons-material/AddToDriveRounded';
import CloudDoneRounded from '@mui/icons-material/CloudDoneRounded';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { QueryState } from '@/components/QueryState';
import { SectionCard } from '@/components/SectionCard';
import { DRIVE_ERROR_MESSAGES } from './drive';
import { useDrive } from './useDrive';

/**
 * Where personal files live. Connecting Drive is optional and only asked for
 * when a feature needs it; this card lets the person manage it at any time.
 */
export function DriveCard({ compact }: { compact?: boolean }) {
  const { status, connect, connectDev, disconnect } = useDrive();
  const [confirming, setConfirming] = useState(false);
  const result = connect.data;

  return (
    <SectionCard
      title="Seus arquivos"
      description="Fotos e documentos pessoais ficam no seu próprio Google Drive, numa pasta deste espaço. Só guardamos a referência."
    >
      <QueryState query={status}>
        {(drive) => (
          <Stack spacing={2}>
            {result?.status === 'error' && (
              <Alert severity="warning">
                {DRIVE_ERROR_MESSAGES[result.code ?? ''] ?? 'Não foi possível conectar.'}
              </Alert>
            )}
            {drive.needsReconnect && (
              <Alert severity="warning">
                O acesso ao seu Drive expirou ou foi revogado. Conecte de novo para ver e enviar
                arquivos.
              </Alert>
            )}
            {drive.connected ? (
              <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <CloudDoneRounded color="success" />
                <Typography variant="body2">
                  Conectado a <strong>{drive.accountEmail ?? 'sua conta'}</strong>
                </Typography>
                {drive.provider === 'local_dev' && (
                  <Chip size="small" variant="soft" color="warning" label="dev" />
                )}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Nenhum armazenamento conectado.
                {!compact &&
                  ' Pedimos acesso apenas aos arquivos que este app criar: nada do resto do seu Drive fica visível.'}
              </Typography>
            )}
            <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', gap: 1.5 }}>
              {!drive.connected && drive.googleConfigured && (
                <Button
                  variant="contained"
                  startIcon={<AddToDriveRounded />}
                  disabled={connect.isPending}
                  onClick={() => connect.mutate()}
                >
                  {drive.needsReconnect ? 'Reconectar Google Drive' : 'Conectar Google Drive'}
                </Button>
              )}
              {!drive.connected && drive.devStorageAvailable && (
                <Button
                  variant="outlined"
                  disabled={connectDev.isPending}
                  onClick={() => connectDev.mutate()}
                >
                  Usar armazenamento local (dev)
                </Button>
              )}
              {drive.connected && (
                <Button color="error" onClick={() => setConfirming(true)}>
                  Desconectar
                </Button>
              )}
            </Stack>
          </Stack>
        )}
      </QueryState>
      <ConfirmDialog
        open={confirming}
        title="Desconectar o Google Drive?"
        description="Seus arquivos continuam no seu Drive, mas o app deixa de acessá-los até você conectar de novo."
        confirmLabel="Desconectar"
        destructive
        onClose={() => setConfirming(false)}
        onConfirm={() => disconnect.mutateAsync()}
      />
    </SectionCard>
  );
}
